import {
	Box,
	Button,
	CircularProgress,
	Grid,
	Link,
	List,
	alpha,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import AuthApis from '../../hepler/authApis.service';
import { toast } from 'react-toastify';
import FipVerifiedHeading from './FipVerifiedHeading';
import styles from './FipDiscoveredAccountsSection.styles';
import sizeFontCustom from './fontSize.styles';
import FipDiscoveredAccounts from './FipDiscoveredAccounts';
import VerifyOtp from './VerifyOtp';
import Drawer from '../Drawer/Drawer';
import { isAccountLinked, getUniqueKey } from '../../utils/helper.utils';
import { isManualAccCheckEnabled } from '../../utils/targetedAccounts.utils';
import { trackEvent } from '../../utils/tracking.utils';
import { postMessage } from '../../utils/common.utils';
import { useTranslation } from '../../hooks/useTranslation';
import { keyboardFocusVisibleSx } from '../../utils/a11y.utils';
import { isSequentialLinkingEnabled } from '../../utils/entityConfig.utils';
import { getEntityThemedCtaButtonSx } from '../../utils/ctaContrast.utils';

function FipDiscoveredAccountsSection(props) {
	const { t } = useTranslation();
	const [linkAccountButtonDisabled, setLinkAccountButtonDisabled] =
		useState(false);
	const theme = useTheme();
	const mobileScreen = useMediaQuery(theme.breakpoints.down('md'));

	const isDark =
		props.customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const authApis = new AuthApis();

	// Opt-in: new discovery UI + sequential linking flow
	const isNewDiscoveryExperience = isSequentialLinkingEnabled(
		props.customDetails?.sdkOptions,
	);

	const fipId = props.accountsSingleFip.fipId;
	const phoneNumber = props.accountsSingleFip.phoneNumber;
	const linkedAccountsRefForList = isNewDiscoveryExperience
		? props.allLinkedAccounts || props.accountState.linkedAccountsFull
		: props.accountState.linkedAccountsFull;
	const linkedAccountsRefForMerge =
		props.allLinkedAccounts || props.accountState.linkedAccountsFull;

	const discoveredFlat = props.accountsSingleFip.discoveredAccounts.flat(1);
	const mergeDiscoveredWithLinkedAccounts = (
		discovered,
		linked,
		currentFipId,
	) => {
		const merged = [...discovered];
		const seen = new Set(merged.map((acc) => getUniqueKey(acc, currentFipId)));

		linked
			.filter((acc) => acc.fipId === currentFipId)
			.forEach((acc) => {
				const key = getUniqueKey(acc, currentFipId);
				if (!seen.has(key)) {
					seen.add(key);
					merged.push({ ...acc, fipId: currentFipId });
				}
			});

		return merged;
	};

	const fipAccountsAll = isNewDiscoveryExperience
		? mergeDiscoveredWithLinkedAccounts(
				discoveredFlat,
				linkedAccountsRefForMerge,
				fipId,
			)
		: discoveredFlat;

	let accountsNotLinkedSet = new Set();
	let accountsNotLinked = fipAccountsAll
		.filter((acc) => {
			const uniqueKey = getUniqueKey(acc, fipId);
			if (!accountsNotLinkedSet.has(uniqueKey)) {
				accountsNotLinkedSet.add(uniqueKey);
				return !isAccountLinked(acc, linkedAccountsRefForList, fipId);
			}
			return false;
		})
		.map((acc) => ({ ...acc, fipId }));

	let accountsLinkedSet = new Set();
	let accountsLinked = fipAccountsAll
		.filter((acc) => {
			const uniqueKey = getUniqueKey(acc, fipId);
			if (!accountsLinkedSet.has(uniqueKey)) {
				accountsLinkedSet.add(uniqueKey);
				return isAccountLinked(acc, linkedAccountsRefForList, fipId);
			}
			return false;
		})
		.map((acc) => ({ ...acc, fipId }));

	const isMandatoryAccount = (acc) =>
		acc?.restrictions?.isMandatory?.toLowerCase() === 'true';

	const isDisableAutoPreselect =
		global.disableAutoPreselect === true ||
		global.disableAutoPreselect === 'true';

	const getInitialDiscoverySelection = () => {
		if (isManualAccCheckEnabled()) return [];
		if (isDisableAutoPreselect) {
			if (global.redirectAccountRestrictions && !global.userClickedAddMore) {
				return accountsNotLinked.filter(isMandatoryAccount);
			}
			return [];
		}
		return accountsNotLinked;
	};

	const [selectedAccounts, setSelectedAccounts] = useState(
		getInitialDiscoverySelection(),
	);
	// eslint-disable-next-line no-unused-vars
	const [checkedItems, setCheckedItems] = useState({});
	const [showAll, setShowAll] = useState(false);

	// Old per-FIP linking state (used when isSequentialLinking === false)
	const [verifyState, setVerifyState] = useState({
		isProceed: true,
		isResend: true,
		isDisable: false,
		OTP: null,
		linkingRefNo: '',
		time: 60,
		linkedStatus: [],
		fipId: '',
	});
	const id = useRef('');

	// Sync selected accounts from parent state
	useEffect(() => {
		const selectedForThisFip = props.accountState.selectedAccounts.filter(
			(acc) =>
				acc.fipId === fipId &&
				(isDisableAutoPreselect ? isMandatoryAccount(acc) : true),
		);
		if (selectedForThisFip.length > 0) {
			setSelectedAccounts(selectedForThisFip);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.accountState.selectedAccounts, fipId]);

	// Notify parent of selection changes (sequential linking flow only)
	useEffect(() => {
		if (!isNewDiscoveryExperience) return;
		props.onSelectedAccountsChange?.(fipId, selectedAccounts);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAccounts, fipId, isNewDiscoveryExperience]);

	// ── Old per-FIP linking logic ─────────────────────────────────────────────
	const isMultiTokenLinking = () => {
		const path = window.location.href.split('/').pop();
		if (path.includes(',')) {
			trackEvent('MULTIPLE_FIP_LINKING', {
				totalFips: path.split(',').length,
				count: path.split(',').length,
			});
		}
		return path.includes(',');
	};

	const initiate = () => {
		setVerifyState((prevState) => {
			const newTime = prevState.time - 1;
			if (newTime === 0) {
				clearInterval(id.current);
				return { ...prevState, time: 60, isResend: true };
			}
			return { ...prevState, time: newTime };
		});
	};

	const setResend = () => {
		clearInterval(id.current);
		id.current = setInterval(initiate, 1000);
	};

	const accountLinking = () => {
		setVerifyState({ ...verifyState, isDisable: true, isResend: false });
		const accountsToLink = selectedAccounts.map(
			({ restrictions, fipId: _fipId, ...accountWithoutRestrictions }) =>
				accountWithoutRestrictions,
		);
		return authApis
			.accountLinking(fipId, accountsToLink)
			.then((res) => {
				setVerifyState({ ...verifyState, isDisable: false });
				if (!res) {
					toast.error(t('errors.general.somethingWentWrong'));
					return res;
				}
				if (res.status === 'SUCCESS') {
					setVerifyState({
						...verifyState,
						isProceed: false,
						isResend: false,
						OTP: '',
						linkingRefNo: res.RefNumber,
						fipId: fipId,
					});
					setResend();
				} else {
					setVerifyState({ ...verifyState, isResend: true });
					if (
						res?.code === undefined ||
						res?.code === null ||
						res?.code === ''
					) {
						toast.error(res?.message);
					} else {
						toast.error(t(`errors.code.${res.code}`));
					}
					if (res.status === 'FAILURE' && res.message === 'Session Error.') {
						setTimeout(() => {
							props.history.push({
								pathname: `/consent-status`,
								state: { statusCode: 20 },
							});
						}, 3000);
					}
				}
				return res;
			})
			.catch(() => {
				setVerifyState({ ...verifyState, isDisable: false, isResend: true });
				toast.error(t('errors.general.failedToLink'));
			});
	};

	const accountConfirmLinking = () => {
		isMultiTokenLinking();
		setVerifyState({ isDisable: true, isResend: false });
		authApis
			.accountConfirmLinking(
				verifyState.linkingRefNo,
				verifyState.OTP,
				verifyState.fipId,
			)
			.then((res) => {
				if (res.status === 'SUCCESS') {
					const linkedAccounts = res?.AccLinkDetails.map(
						(acc) => acc.accRefNumber,
					);
					const newLinkedAccounts = res?.AccLinkDetails.map((apiAccount) => {
						const matchingSelectedAccount = selectedAccounts.find(
							(selAcc) => selAcc.accRefNumber === apiAccount.accRefNumber,
						);
						return {
							...matchingSelectedAccount,
							...apiAccount,
							fipId: fipId,
						};
					});
					setVerifyState({
						...verifyState,
						isDisable: false,
						isResend: true,
						linkedStatus: linkedAccounts,
					});
					if (props.setAllLinkedAccounts) {
						props.setAllLinkedAccounts((prev) => [
							...prev,
							...newLinkedAccounts,
						]);
					}
					if (props.onSessionAccountsLinked) {
						props.onSessionAccountsLinked(newLinkedAccounts);
					}
					toast.success(res.message);
					props.setAccountsNotLinkedLength(
						(prev) => prev - linkedAccounts.length,
					);
					props.setSelectedAccountLinkedLength(
						(prev) => prev + linkedAccounts.length,
					);
				} else {
					setVerifyState({ ...verifyState, OTP: '' });
					if (
						res?.code === undefined ||
						res?.code === null ||
						res?.code === ''
					) {
						toast.error(res?.message);
					} else {
						toast.error(t(`errors.code.${res.code}`));
					}
					if (res.status === 'FAILURE') {
						postMessage('session', 'FAILURE');
					}
				}
			});
	};

	const handleVerify = () => {
		setLinkAccountButtonDisabled(true);
		accountLinking().finally(() => {
			setLinkAccountButtonDisabled(false);
		});
	};
	// ─────────────────────────────────────────────────────────────────────────

	const handleCheckedState = (event, account) => {
		selectUnselectAcc(account);
		const { name, checked } = event.target;
		setCheckedItems((prevCheckedItems) => ({
			...prevCheckedItems,
			[name]: checked,
		}));
	};

	const selectUnselectAcc = (account) => {
		let data = [...selectedAccounts];
		const existingAccountIndex = data.findIndex(
			(acc) => acc.accRefNumber === account.accRefNumber,
		);
		if (existingAccountIndex !== -1) {
			data.splice(existingAccountIndex, 1);
		} else {
			data.push({ ...account, fipId });
		}
		setSelectedAccounts(data);
	};

	const toggleShowAll = () => {
		setShowAll((prev) => !prev);
	};

	const renderLinkAccountSection = () => {
		if (
			verifyState.linkedStatus?.length !== 0 ||
			accountsNotLinked.length === 0
		) {
			return null;
		}

		return (
			<Box sx={{ mt: '0.5rem' }}>
				{verifyState.isProceed ? (
					<Box mx={'1rem'}>
						<Button
							fullWidth
							variant="contained"
							onClick={handleVerify}
							disabled={
								selectedAccounts.length === 0 || linkAccountButtonDisabled
							}
							sx={{
								textTransform: 'capitalize',
								...sizeFontCustom.subHeading,
								my: 1,
								...getEntityThemedCtaButtonSx(props.customDetails, isDark),
							}}
						>
							{linkAccountButtonDisabled ? (
								<CircularProgress
									size={24}
									sx={{ color: theme.palette.primary.white }}
								/>
							) : (
								t('common.buttons.linkAccount')
							)}
						</Button>
					</Box>
				) : !mobileScreen ? (
					<VerifyOtp
						phoneNumber={phoneNumber}
						setVerifyState={setVerifyState}
						verifyState={verifyState}
						handleVerify={handleVerify}
						accountConfirmLinking={accountConfirmLinking}
						linkAccountButtonDisabled={linkAccountButtonDisabled}
						customDetails={props.customDetails}
					/>
				) : (
					<Drawer
						open={!verifyState.isProceed}
						setOpen={() => setVerifyState({ ...verifyState, isProceed: true })}
						medWidth={'30%'}
						closeBtnTop="0.35rem"
						anchor={'bottom'}
						height={'30vh'}
						customDetails={props.customDetails}
						showCloseBtn={!isNewDiscoveryExperience && !mobileScreen}
					>
						<VerifyOtp
							phoneNumber={phoneNumber}
							setVerifyState={setVerifyState}
							verifyState={verifyState}
							handleVerify={handleVerify}
							accountConfirmLinking={accountConfirmLinking}
							linkAccountButtonDisabled={linkAccountButtonDisabled}
							customDetails={props.customDetails}
						/>
					</Drawer>
				)}
			</Box>
		);
	};

	if (fipAccountsAll.length === 0) {
		return null;
	}

	if (!isNewDiscoveryExperience) {
		return (
			<Grid item xs={12} sm={8} md={8} lg={5.5}>
				<List
					component="div"
					sx={{
						background: isDark ? '#2D2D2D' : theme.palette.primary.white,
						color: isDark ? '#FFFFFF' : '#000000',
						justifyContent: 'start',
						alignItems: 'start',
						borderRadius: '1rem',
						padding: '0.5rem',
						border: 'none',
					}}
				>
					<FipVerifiedHeading
						allLinked={
							accountsNotLinked.length === 0 && accountsLinked.length > 0
						}
						isVerified={verifyState.linkedStatus}
						fipId={fipId}
						phoneNumber={phoneNumber}
						areAccountsDiscovered={fipAccountsAll.length > 0}
						customDetails={props.customDetails}
					/>
					{fipAccountsAll.length > 0 && (
						<List
							component="ul"
							disablePadding
							aria-label={t('accountDiscovery.messages.accountsDiscovered')}
							tabIndex={!showAll ? 0 : undefined}
							sx={{
								...styles.discoveredAccounts,
								listStyle: 'none',
								maxHeight: showAll ? 'auto' : '10rem',
								'&::-webkit-scrollbar-thumb': {
									backgroundColor: alpha(theme.palette.primary.main, 0.5),
									borderRadius: '10px',
								},
							}}
						>
							{accountsNotLinked.length > 0
								? accountsNotLinked.map((account) => (
										<FipDiscoveredAccounts
											key={account.accRefNumber}
											account={account}
											fipId={fipId}
											handleCheckedState={handleCheckedState}
											selectedAccounts={selectedAccounts}
											isVerified={verifyState.linkedStatus}
											isLinked={false}
											customDetails={props.customDetails}
											useNewDiscoveryUI={false}
										/>
									))
								: accountsLinked.map((account) => (
										<FipDiscoveredAccounts
											key={account.accRefNumber}
											account={account}
											fipId={fipId}
											isVerified={verifyState.linkedStatus}
											handleCheckedState={handleCheckedState}
											selectedAccounts={selectedAccounts}
											isLinked
											customDetails={props.customDetails}
											useNewDiscoveryUI={false}
										/>
									))}
						</List>
					)}
					{accountsNotLinked.length === 0 && (
						<Box
							display={accountsLinked.length - 2 > 0 ? 'block' : 'none'}
							textAlign={'start'}
						>
							<Link
								component="button"
								type="button"
								display="block"
								underline="none"
								sx={{
									cursor: 'pointer',
									...sizeFontCustom.subHeading,
									mt: '0.1rem',
									ml: '1rem',
									color: isDark ? '#FFFFFF' : 'inherit',
									background: 'none',
									border: 'none',
									padding: '0.25rem',
									borderRadius: '4px',
									...keyboardFocusVisibleSx,
									font: 'inherit',
									textAlign: 'start',
								}}
								onClick={toggleShowAll}
							>
								{showAll
									? t('common.buttons.showLess')
									: `+ ${accountsLinked.length - 2} ${t('common.buttons.showMore')}`}
							</Link>
						</Box>
					)}
					<Box
						display={accountsNotLinked.length - 2 > 0 ? 'block' : 'none'}
						textAlign={'start'}
					>
						<Link
							component="button"
							type="button"
							display="block"
							underline="none"
							sx={{
								cursor: 'pointer',
								...sizeFontCustom.subHeading,
								mt: '0.1rem',
								ml: '1rem',
								color: isDark ? '#FFFFFF' : 'inherit',
								background: 'none',
								border: 'none',
								padding: '0.25rem',
								borderRadius: '4px',
								...keyboardFocusVisibleSx,
								font: 'inherit',
								textAlign: 'start',
							}}
							onClick={toggleShowAll}
						>
							{showAll
								? t('common.buttons.showLess')
								: `+ ${accountsNotLinked.length - 2} ${t('common.buttons.showMore')}`}
						</Link>
					</Box>
					{renderLinkAccountSection()}
				</List>
			</Grid>
		);
	}

	return (
		<Grid item xs={12}>
			<List
				component="div"
				sx={{
					background: isDark ? '#2D2D2D' : theme.palette.primary.white,
					color: isDark ? '#FFFFFF' : '#000000',
					justifyContent: 'start',
					alignItems: 'start',
					borderRadius: '1rem',
					padding: '24px 16px',
					border: `1px solid ${isDark ? '#3D3D3D' : '#D9D9D9'}`,
					width: '100%',
				}}
			>
				<FipVerifiedHeading
					allLinked={
						accountsNotLinked.length === 0 && accountsLinked.length > 0
					}
					isVerified={null}
					fipId={fipId}
					phoneNumber={phoneNumber}
					areAccountsDiscovered={fipAccountsAll.length > 0}
					selectedCount={selectedAccounts.length}
					totalCount={fipAccountsAll.length}
					showSelectionCount
					customDetails={props.customDetails}
				/>
				{fipAccountsAll.length > 0 && (
					<List
						component="ul"
						disablePadding
						aria-label={t('accountDiscovery.messages.accountsDiscovered')}
						sx={{
							...styles.discoveredAccounts,
							listStyle: 'none',
						}}
					>
						{(showAll ? fipAccountsAll : fipAccountsAll.slice(0, 3)).map(
							(account) => {
								const isLinked = isAccountLinked(
									account,
									linkedAccountsRefForList,
									fipId,
								);
								return (
									<FipDiscoveredAccounts
										key={account.accRefNumber}
										account={account}
										fipId={fipId}
										handleCheckedState={handleCheckedState}
										selectedAccounts={selectedAccounts}
										isVerified={null}
										isLinked={isLinked}
										customDetails={props.customDetails}
										useNewDiscoveryUI
									/>
								);
							},
						)}
					</List>
				)}
				<Box
					display={fipAccountsAll.length > 3 ? 'block' : 'none'}
					textAlign={'start'}
				>
					<Link
						component="button"
						type="button"
						display="block"
						underline="none"
						sx={{
							cursor: 'pointer',
							...sizeFontCustom.subHeading,
							fontSize: '14px',
							mt: '0.75rem',
							ml: '1rem',
							color: isDark ? '#FFFFFF' : 'inherit',
							background: 'none',
							border: 'none',
							padding: '0.25rem',
							borderRadius: '4px',
							...keyboardFocusVisibleSx,
							font: 'inherit',
							textAlign: 'start',
						}}
						onClick={toggleShowAll}
					>
						{showAll
							? t('common.buttons.showLess')
							: `+ ${fipAccountsAll.length - 3} ${t('common.buttons.showMore')}`}
					</Link>
				</Box>
			</List>
		</Grid>
	);
}

export default FipDiscoveredAccountsSection;
